import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  FullscreenLoader,
} from '@cointervue/design-system/components';
import { isChunkLoadError, handleChunkReload } from '@cointervue/shared';
import { coIntervueLogo } from '@assets/Icons';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

function DefaultFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center shadow-sm border border-border">
        <CardHeader className="space-y-2 pb-2">
          <CardTitle className="text-xl font-semibold text-foreground">
            Something went wrong
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Please try again. If the problem persists, contact support.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2 pb-6 flex justify-center">
          <Button
            onClick={() => {
              window.location.reload();
            }}
            className="w-auto px-6 py-2"
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      fallbackRender={({ error }) => {
        if (isChunkLoadError(error)) {
          handleChunkReload(error);
          return <FullscreenLoader logo={coIntervueLogo} />;
        }
        return fallback ? <>{fallback}</> : <DefaultFallback />;
      }}
      onError={(error, info) => {
        if (!isChunkLoadError(error)) {
          console.error('Uncaught error in ErrorBoundary:', error, info);
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
